import AuthorCard from "@components/Cards/AuthorCard";
import { ArticleGridCards } from "@components/Cards/Layout/ArticleGridCards";
import MainContainer from "@components/Container/MainContainer";
import { LOCALHOST } from "@config/MetaTagsConstants";
import { AdsLb2, AdsLb3 } from "@elements/Advertisement/variant/AdsLb";
import { getGTMData } from "@helper/getMetaData";
import { ProcessedArticleData } from "@helper/transformer/useOsResponse";
import { usePageNumberSync } from "@hooks/useLoadMore/usePageNumberSync";
import { useWindowSize } from "@hooks/useWindowSize/useWindowSize";
import { Route } from "@route-types/Author/+types/AuthorPage";
import ErrorPage from "@routes/StatusHandler/StatusHandler";
import {
  fetchContext,
  ResponseType,
  type TRouteWithoutRedirect,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { createMetaTags, type MetaTagsProps } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type { GADataProps } from "@typings/PageMetaData";
import { AuthorPayloadResponseType } from "@typings/PagePayloadType";
import React from "react";
import { useMemo } from "react";
import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import { twMerge } from "tailwind-merge";

import { fetchAuthorData } from "./Author.server";
import AuthorInfiniteScroll from "./AuthorInfiniteScroll";

export function meta(args: Route.MetaArgs) {
  const dataLoaderResponse = args.loaderData as TRouteWithRedirect<
    AuthorPayloadResponseType,
    string
  >;
  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const metaData = dataLoaderResponse.payload.metaTags as MetaTagsProps;
    return createMetaTags(metaData);
  }
}

export default function AuthorPage(): React.ReactElement {
  const dataLoaderResponse: TRouteWithoutRedirect<
    AuthorPayloadResponseType,
    string
  > = useLoaderData();

  const { isScreenMD } = useWindowSize();
  const { setPageRef } = usePageNumberSync();
  const authorArticles = useMemo(
    () => (dataLoaderResponse.type ? [] : dataLoaderResponse.payload.data),
    [dataLoaderResponse],
  );
  const authorAlias = dataLoaderResponse.type
    ? ""
    : dataLoaderResponse.payload.authorAlias;
  const epaperCoverImageUrl = dataLoaderResponse.type
    ? ""
    : dataLoaderResponse.payload.epaperCoverImage.coverImageUri;

  const loadedAuthorArticles = useMemo(() => {
    const sections: ProcessedArticleData[][] = [];

    if (isScreenMD) {
      authorArticles.forEach((context, index) => {
        if (index % 8 === 0) sections.push([]);
        sections[sections.length - 1].push(context);
      });
      return sections;
    }

    authorArticles.forEach((context, index) => {
      const chunkIndex = index < 4 ? 0 : Math.floor((index - 4) / 8) + 1;
      if (!sections[chunkIndex]) {
        sections[chunkIndex] = [];
      }
      sections[chunkIndex].push(context);
    });

    return sections;
  }, [authorArticles, isScreenMD]);

  const author = useMemo(() => {
    const primaryAuthor = authorArticles[0]?.authors?.[0];
    if (!primaryAuthor) {
      return undefined;
    }

    for (const auth of authorArticles[0]?.authors || []) {
      if (auth.urlPath?.toLowerCase().includes(authorAlias.toLowerCase())) {
        return auth;
      }
    }

    return primaryAuthor;
  }, [authorAlias, authorArticles]);

  if (dataLoaderResponse.type) {
    return <ErrorPage response={dataLoaderResponse} />;
  }

  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const sectionName = "author";

  return (
    <MainContainer
      pageAdTargetValue={pageAdTargetValue}
      showEpaperBanner={true}
      epaperCoverImageUrl={epaperCoverImageUrl}
      sectionName={sectionName}
    >
      <div className="flex flex-col lg:flex-row justify-between lg:mt-10">
        <div className="w-full mt-xxm lg:mt-0">
          {author && (
            <AuthorCard
              author={author}
              displayWidth={isScreenMD ? 200 : 120}
              displayHeight={isScreenMD ? 200 : 120}
              imageWrapper={isScreenMD ? "max-w-50" : "max-w-30"}
              authorCardWrapper="px-3 xl:px-0 lg:pb-5 xmd:pt-0 lg:pt-7 max-w-7xl m-auto"
            />
          )}
          {loadedAuthorArticles.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              id={`page-${sectionIndex + 1}`}
              ref={setPageRef(sectionIndex + 1)}
            >
              <ArticleGridCards
                article={section}
                articleIndex={sectionIndex}
                rootClassName={twMerge(
                  "px-3 xl:p-0 gap-3 md:inline-grid md:grid-cols-4 xmd:inline-grid lg:grid xmd:pb-10 lg:max-w-7xl bg-white m-auto",
                  loadedAuthorArticles.length - 1 === sectionIndex && "mb-5",
                )}
                cardWrapper="gap-2 xmd:py-0 md:flex-col md:max-w-[210px] xmd:max-w-full"
                imageWrapperStyle="md:max-w-[171px] xmd:w-full"
              />

              {sectionIndex < loadedAuthorArticles.length - 1 && (
                <React.Fragment key={sectionIndex}>
                  {sectionIndex == 0 ? (
                    <AdsLb2
                      sectionName={sectionName}
                      pageAdTargetType={pageAdTargetValue}
                      rootClassName={
                        "mb-5 xmd:my-5 relative w-full bg-grey-950 py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
                      }
                      index={sectionIndex}
                    />
                  ) : (
                    <AdsLb3
                      sectionName={sectionName}
                      pageAdTargetType={pageAdTargetValue}
                      rootClassName={
                        "mb-5 xmd:my-5 relative w-full bg-grey-950 py-xxm pt-2.5 pb-5 md:px-2 md:py-xxs lg:pt-4 lg:pb-6 lg:px-0"
                      }
                      index={sectionIndex}
                    />
                  )}
                </React.Fragment>
              )}
            </div>
          ))}

          <AuthorInfiniteScroll
            authorArticles={authorArticles}
            authorAlias={authorAlias}
            pageAdTargetValue={pageAdTargetValue}
            sectionName={sectionName}
            setPageRef={setPageRef}
            initialPageCount={loadedAuthorArticles.length}
          />
        </div>
      </div>
    </MainContainer>
  );
}

export async function loader({ params, request }: Route.LoaderArgs) {
  const authorName = params.level1;
  // we append `author` as required by the CAAS query
  const fullAuthorUrl = "/author/" + authorName;
  const data = await fetchAuthorData(fullAuthorUrl);

  const url = new URL(request.url, LOCALHOST);
  const gtmData = getGTMData({
    title: `${authorName}_Index`,
    level2: "author",
    url: url.toString(),
  } as GADataProps);

  return {
    ...data,
    gtm: gtmData,
  };
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occured during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<AuthorPayloadResponseType, string>
  >;
}
